const FACILITY_PREFIX = "/facility";
const PORTFOLIO_PREFIX = "/portfolio";
const ADMIN_PREFIX = "/admin";
const COMMON_PREFIX = "/common";

const ensureLeadingSlash = (value = "") => {
  if (!value) {
    return "/";
  }

  return value.startsWith("/") ? value : `/${value}`;
};

const stripTrailingSlash = (value = "") => {
  if (!value || value === "/") {
    return value || "/";
  }

  return value.endsWith("/") ? value.slice(0, -1) : value;
};

const normalizeQuery = (query) => {
  if (!query) {
    return "";
  }

  if (typeof query === "string") {
    return query.startsWith("?") ? query : `?${query}`;
  }

  if (query instanceof URLSearchParams) {
    const serialized = query.toString();
    return serialized ? `?${serialized}` : "";
  }

  if (typeof query === "object") {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, value);
      }
    });
    const serialized = params.toString();
    return serialized ? `?${serialized}` : "";
  }

  return "";
};

const splitPathAndQuery = (input = "") => {
  const [pathname = "", query = ""] = String(input).split("?");
  return {
    pathname: pathname || "",
    search: query ? `?${query}` : "",
  };
};

const replacePathParams = (pathTemplate = "", params = {}) => {
  return Object.entries(params).reduce((accumulator, [key, value]) => {
    return accumulator.replace(`:${key}`, value);
  }, pathTemplate);
};

export const isFacilityPath = (pathname = "") => pathname.startsWith(FACILITY_PREFIX);

export const isPortfolioPath = (pathname = "") => pathname.startsWith(PORTFOLIO_PREFIX);

export const isAdminPath = (pathname = "") => pathname.startsWith(ADMIN_PREFIX);

export const isCommonPath = (pathname = "") => pathname.startsWith(COMMON_PREFIX);

export const getLegacyLeaf = (pathname = "", viewType) => {
  const normalizedPath = stripTrailingSlash(ensureLeadingSlash(pathname));

  if (viewType === "facility" && normalizedPath.startsWith(`${FACILITY_PREFIX}/`)) {
    return normalizedPath.slice(`${FACILITY_PREFIX}/`.length);
  }

  if (viewType === "portfolio" && normalizedPath.startsWith(`${PORTFOLIO_PREFIX}/`)) {
    return normalizedPath.slice(`${PORTFOLIO_PREFIX}/`.length);
  }

  return "";
};

export const getContextualRoutePattern = (path, viewType) => {
  if (!path) {
    return path;
  }

  if (viewType === "facility") {
    const leaf = getLegacyLeaf(path, "facility");
    return `${FACILITY_PREFIX}/:companyId/:facilityId/${leaf}`;
  }

  if (viewType === "portfolio") {
    const leaf = getLegacyLeaf(path, "portfolio");
    return `${PORTFOLIO_PREFIX}/:companyId/${leaf}`;
  }

  return path;
};

export const getFacilityContext = (source) => {
  if (!source) {
    return {
      companyId: null,
      facilityId: null,
    };
  }

  if ("companyId" in source || "facilityId" in source) {
    return {
      companyId: source.companyId ? String(source.companyId) : null,
      facilityId: source.facilityId ? String(source.facilityId) : null,
    };
  }

  const companyId = source?.Company?.selectedId ?? source?.companyId ?? null;
  const facilityId = source?.Facility?.selected?.Id ?? source?.facilityId ?? null;

  return {
    companyId: companyId ? String(companyId) : null,
    facilityId: facilityId ? String(facilityId) : null,
  };
};

export const getPortfolioContext = (source) => {
  if (!source) {
    return {
      companyId: null,
    };
  }

  if ("companyId" in source) {
    return {
      companyId: source.companyId ? String(source.companyId) : null,
    };
  }

  const companyId = source?.Company?.selectedId ?? source?.companyId ?? null;

  return {
    companyId: companyId ? String(companyId) : null,
  };
};

export const buildFacilityPath = (leaf = "", options = {}) => {
  const { companyId, facilityId, params, query } = options;

  if (!companyId || !facilityId) {
    return null;
  }

  const normalizedLeaf = String(leaf || "").replace(/^\/+/, "");
  const pathTemplate = `${FACILITY_PREFIX}/${companyId}/${facilityId}${normalizedLeaf ? `/${normalizedLeaf}` : ""}`;
  const pathname = replacePathParams(pathTemplate, params);

  return `${stripTrailingSlash(pathname)}${normalizeQuery(query)}`;
};

export const buildPortfolioPath = (leaf = "", options = {}) => {
  const { companyId, params, query } = options;

  if (!companyId) {
    return null;
  }

  const normalizedLeaf = String(leaf || "").replace(/^\/+/, "");
  const pathTemplate = `${PORTFOLIO_PREFIX}/${companyId}${normalizedLeaf ? `/${normalizedLeaf}` : ""}`;
  const pathname = replacePathParams(pathTemplate, params);

  return `${stripTrailingSlash(pathname)}${normalizeQuery(query)}`;
};

export const buildPathFromConfigPath = (path = "", viewType, context = {}, query) => {
  if (!path) {
    return path;
  }

  if (viewType === "facility") {
    const leaf = getLegacyLeaf(path, "facility");
    return buildFacilityPath(leaf, {
      companyId: context.companyId,
      facilityId: context.facilityId,
      query,
    });
  }

  if (viewType === "portfolio") {
    const leaf = getLegacyLeaf(path, "portfolio");
    return buildPortfolioPath(leaf, {
      companyId: context.companyId,
      query,
    });
  }

  return path;
};

export const getLeafFromPathname = (pathname = "", viewType) => {
  const normalizedPath = stripTrailingSlash(ensureLeadingSlash(pathname));

  if (viewType === "facility") {
    const facilityMatch = normalizedPath.match(/^\/facility\/[^/]+\/[^/]+\/(.+)$/);
    if (facilityMatch?.[1]) {
      return facilityMatch[1];
    }

    return getLegacyLeaf(normalizedPath, "facility");
  }

  if (viewType === "portfolio") {
    const portfolioMatch = normalizedPath.match(/^\/portfolio\/[^/]+\/(.+)$/);
    if (portfolioMatch?.[1]) {
      return portfolioMatch[1];
    }

    return getLegacyLeaf(normalizedPath, "portfolio");
  }

  return "";
};

export const extractContextFromPath = (input = {}) => {
  if (typeof input === "string") {
    const { pathname } = splitPathAndQuery(input);

    const facilityMatch = pathname.match(/^\/facility\/([^/]+)\/([^/]+)(?:\/|$)/);
    if (facilityMatch) {
      return {
        viewType: "facility",
        companyId: facilityMatch[1],
        facilityId: facilityMatch[2],
      };
    }

    const portfolioMatch = pathname.match(/^\/portfolio\/([^/]+)(?:\/|$)/);
    if (portfolioMatch) {
      return {
        viewType: "portfolio",
        companyId: portfolioMatch[1],
      };
    }

    return {
      viewType: null,
      companyId: null,
      facilityId: null,
    };
  }

  return {
    viewType: input.facilityId ? "facility" : input.companyId ? "portfolio" : null,
    companyId: input.companyId ? String(input.companyId) : null,
    facilityId: input.facilityId ? String(input.facilityId) : null,
  };
};

export const getAllowedPortfolioCompanyIds = (authState = {}) => {
  const userFacilities = Array.isArray(authState?.user_facilities) ? authState.user_facilities : [];
  const companyEntries = Array.isArray(authState?.company) ? authState.company : [];
  const companyIds = new Set();

  userFacilities.forEach((facility) => {
    if (facility?.company_id !== undefined && facility?.company_id !== null) {
      companyIds.add(String(facility.company_id));
    }
  });

  companyEntries.forEach((company) => {
    const companyId = company?.id ?? company?.Id ?? company?.company_id ?? company?.value;
    if (companyId !== undefined && companyId !== null) {
      companyIds.add(String(companyId));
    }
  });

  return Array.from(companyIds);
};

export const toCanonicalContextPath = (input, context = {}) => {
  const value = typeof input === "string" ? input : input?.pathname || "";
  const search = typeof input === "string" ? splitPathAndQuery(input).search : input?.search || "";
  const { pathname } = splitPathAndQuery(value);

  if (isFacilityPath(pathname)) {
    const leaf = getLeafFromPathname(pathname, "facility");
    return buildFacilityPath(leaf, {
      companyId: context.companyId,
      facilityId: context.facilityId,
      query: search,
    });
  }

  if (isPortfolioPath(pathname)) {
    const leaf = getLeafFromPathname(pathname, "portfolio");
    return buildPortfolioPath(leaf, {
      companyId: context.companyId,
      query: search,
    });
  }

  return pathname ? `${pathname}${search}` : null;
};

export const buildPathForCurrentLeaf = (locationLike, context = {}) => {
  const pathname = locationLike?.pathname || "";
  const search = locationLike?.search || "";

  if (isFacilityPath(pathname)) {
    const leaf = getLeafFromPathname(pathname, "facility");
    return buildFacilityPath(leaf || "dashboard", {
      companyId: context.companyId,
      facilityId: context.facilityId,
      query: search,
    });
  }

  if (isPortfolioPath(pathname)) {
    const leaf = getLeafFromPathname(pathname, "portfolio");
    return buildPortfolioPath(leaf || "dashboard", {
      companyId: context.companyId,
      query: search,
    });
  }

  return null;
};

export const getScopedErrorPath = (viewType, errorType, context = {}) => {
  if (viewType === "facility") {
    return buildFacilityPath(errorType, context) || "/switch-view";
  }

  if (viewType === "portfolio") {
    return buildPortfolioPath(errorType, context) || "/switch-view";
  }

  if (viewType === "admin") {
    return `/admin/${errorType}`;
  }

  if (viewType === "common") {
    return `/${errorType}`;
  }

  return "/switch-view";
};

export const resolveLegacyContextRedirect = (input, state = {}) => {
  const { pathname, search } = typeof input === "string" ? splitPathAndQuery(input) : input;

  if (isFacilityPath(pathname)) {
    const context = getFacilityContext(state);
    if (!context.companyId || !context.facilityId) {
      return null;
    }

    return toCanonicalContextPath({ pathname, search }, context);
  }

  if (isPortfolioPath(pathname)) {
    const context = getPortfolioContext(state);
    if (!context.companyId) {
      return null;
    }

    return toCanonicalContextPath({ pathname, search }, context);
  }

  return null;
};

export const isLegacyContextPath = (pathname = "") => {
  if (pathname.startsWith(`${FACILITY_PREFIX}/`)) {
    return !/^\/facility\/[^/]+\/[^/]+(?:\/|$)/.test(pathname);
  }

  if (pathname.startsWith(`${PORTFOLIO_PREFIX}/`)) {
    return !/^\/portfolio\/[^/]+(?:\/|$)/.test(pathname) || /^\/portfolio\/(dashboard|403|404)(?:\/|$)/.test(pathname);
  }

  return false;
};

export const isRouteContextReady = (viewType, state = {}) => {
  if (viewType === "facility") {
    const context = getFacilityContext(state);
    return Boolean(context.companyId && context.facilityId);
  }

  if (viewType === "portfolio") {
    const context = getPortfolioContext(state);
    return Boolean(context.companyId);
  }

  return true;
};

export const matchesRouteFamily = (pathname = "", viewType, leafPrefix = "") => {
  const normalizedLeafPrefix = String(leafPrefix || "").replace(/^\/+/, "");

  if (viewType === "facility") {
    const canonicalPattern = new RegExp(`^/facility/[^/]+/[^/]+/${normalizedLeafPrefix}`);
    const legacyPattern = new RegExp(`^/facility/${normalizedLeafPrefix}`);
    return canonicalPattern.test(pathname) || legacyPattern.test(pathname);
  }

  if (viewType === "portfolio") {
    const canonicalPattern = new RegExp(`^/portfolio/[^/]+/${normalizedLeafPrefix}`);
    const legacyPattern = new RegExp(`^/portfolio/${normalizedLeafPrefix}`);
    return canonicalPattern.test(pathname) || legacyPattern.test(pathname);
  }

  return pathname.startsWith(`/${viewType}/${normalizedLeafPrefix}`);
};
